/**
 * WebGL2 context, capability detection, and the small helper layer everything
 * else in render/ is built on.
 *
 * WebGL2 is the shipping path. WebGPU is probed but never depended on — see
 * docs/01-architecture.md §6.5. If a capability is missing we degrade, we do
 * not throw, because a black screen is a worse bug than a slightly uglier one.
 */

export interface GLCaps {
  webgl2: boolean;
  colorBufferFloat: boolean;
  textureFloatLinear: boolean;
  maxTextureSize: number;
  maxTextureUnits: number;
  instancedArrays: boolean;
  /** Probed for the optional compute paths; never required. */
  webgpu: boolean;
  rendererName: string;
}

export class GLContext {
  readonly gl: WebGL2RenderingContext;
  readonly canvas: HTMLCanvasElement;
  readonly caps: GLCaps;

  /** CSS pixel size. */
  width = 1;
  height = 1;
  /** Backing-store size (CSS × dpr, clamped). */
  drawWidth = 1;
  drawHeight = 1;
  dpr = 1;

  private unitQuad: WebGLVertexArrayObject | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      // The compositor can skip a copy when it knows we redraw every frame.
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
      desynchronized: true,
    } as WebGLContextAttributes);

    if (!gl) {
      throw new Error(
        'WebGL2 is required and is unavailable. In Chrome, check that hardware ' +
          'acceleration is enabled in Settings → System.',
      );
    }
    this.gl = gl;
    this.caps = detectCaps(gl);
    this.resize();
  }

  /**
   * Match the backing store to the display. DPR is clamped to 2 — a pixel-art
   * game gains nothing above that and a 3x retina buffer costs 2.25x the fill.
   */
  resize(): boolean {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    const dw = Math.max(1, Math.floor(w * dpr));
    const dh = Math.max(1, Math.floor(h * dpr));
    if (dw === this.drawWidth && dh === this.drawHeight) return false;
    this.canvas.width = dw;
    this.canvas.height = dh;
    this.width = w;
    this.height = h;
    this.drawWidth = dw;
    this.drawHeight = dh;
    this.dpr = dpr;
    return true;
  }

  /** A VAO drawing a full-screen triangle strip in clip space. */
  quad(): WebGLVertexArrayObject {
    if (this.unitQuad) return this.unitQuad;
    const gl = this.gl;
    const vao = gl.createVertexArray()!;
    const buf = gl.createBuffer()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    this.unitQuad = vao;
    return vao;
  }
}

function detectCaps(gl: WebGL2RenderingContext): GLCaps {
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  return {
    webgl2: true,
    colorBufferFloat: gl.getExtension('EXT_color_buffer_float') !== null,
    textureFloatLinear: gl.getExtension('OES_texture_float_linear') !== null,
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    maxTextureUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as number,
    instancedArrays: true,
    webgpu: typeof navigator !== 'undefined' && 'gpu' in navigator,
    rendererName: debugInfo
      ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
      : 'unknown',
  };
}

// ------------------------------------------------------------------ shaders

export function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
  label: string,
): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, source);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? '';
    gl.deleteShader(sh);
    throw new Error(`shader "${label}" failed to compile:\n${annotate(source, log)}`);
  }
  return sh;
}

export function createProgram(
  gl: WebGL2RenderingContext,
  vertSrc: string,
  fragSrc: string,
  label: string,
): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc, `${label}.vert`);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc, `${label}.frag`);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog) ?? '';
    gl.deleteProgram(prog);
    throw new Error(`program "${label}" failed to link:\n${log}`);
  }
  return prog;
}

/** Prefix each source line with its number so GLSL errors are findable. */
function annotate(src: string, log: string): string {
  const lines = src.split('\n');
  const numbered = lines.map((l, i) => `${String(i + 1).padStart(3)} | ${l}`).join('\n');
  return `${log}\n${numbered}`;
}

/** Cache uniform locations; getUniformLocation in a draw loop is a real cost. */
export class Uniforms {
  private map = new Map<string, WebGLUniformLocation | null>();
  constructor(
    private gl: WebGL2RenderingContext,
    private prog: WebGLProgram,
  ) {}

  loc(name: string): WebGLUniformLocation | null {
    let l = this.map.get(name);
    if (l === undefined) {
      l = this.gl.getUniformLocation(this.prog, name);
      this.map.set(name, l);
    }
    return l;
  }

  f1(name: string, v: number): void {
    const l = this.loc(name);
    if (l) this.gl.uniform1f(l, v);
  }
  i1(name: string, v: number): void {
    const l = this.loc(name);
    if (l) this.gl.uniform1i(l, v);
  }
  f2(name: string, x: number, y: number): void {
    const l = this.loc(name);
    if (l) this.gl.uniform2f(l, x, y);
  }
  f3(name: string, x: number, y: number, z: number): void {
    const l = this.loc(name);
    if (l) this.gl.uniform3f(l, x, y, z);
  }
  f4(name: string, x: number, y: number, z: number, w: number): void {
    const l = this.loc(name);
    if (l) this.gl.uniform4f(l, x, y, z, w);
  }
}

// ----------------------------------------------------------------- textures
//
// There is no texture helper here, and that is the finding of #125 rather than
// an omission. This module used to export `createTexture`/`TexOptions` and a
// `Framebuffer` built on them; nothing ever constructed the framebuffer, and
// `createTexture` was reached only by that framebuffer.
//
// The reason no renderer adopted it is visible in the four sites that allocate
// textures — `terrain.ts`'s palette, props and chunk textures, `light.ts`'s
// window and unlit texture, `pixelBodies.ts`'s atlas and palette. Every one of
// them needs `texStorage2D` **and** a `texSubImage2D` upload, and the helper
// did only the first: it accepted `format` and `type` and then discarded both
// (`void format; void type;`), because `texStorage2D` does not take them. So it
// could allocate storage and never fill it, and each caller still had to write
// the upload by hand. Six inline lines beat five plus a call that cannot
// finish the job, four times over.
//
// Every pass this renderer has draws to the default framebuffer. The offscreen
// candidates in docs/01 §6.2 — the particle pass, the ¼-res gas pass and the
// post chain — are unbuilt, and §6.3 records that the light field was
// deliberately moved *off* the GPU into `sim/light.ts` because a framebuffer
// pass cannot take a work quota, cannot respect chunk sleeping, and cannot
// answer "is the player standing in the dark". When an offscreen pass is
// actually written, it should bring a helper that consults
// `caps.colorBufferFloat` and degrades — the deleted one checked completeness
// and *threw*, which is the black screen this file's header forbids.
